import { Injectable, NgZone } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { NutritionAmbitionApiService, RegisterDeviceRequest } from './nutrition-ambition-api.service';
import { PlatformService } from './platform.service';
import { AccountsService } from './accounts.service';
import { NotificationPermissionModalComponent } from '../components/notification-permission-modal/notification-permission-modal.component';

// Storage keys for Capacitor Preferences
const STORAGE_KEYS = {
  INSTALL_ID: 'installId',
  HAS_SEEN_NOTIFICATION_PROMPT: 'hasSeenNotificationPermissionPrompt'
};

/**
 * Service for managing push notifications.
 * Handles FCM token lifecycle, permission requests, and backend registration.
 */
@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private isInitialized = false;
  private accountSubscriptionSetUp = false;

  /**
   * Pending notification action data to be consumed by the chat page.
   * Uses a BehaviorSubject so the chat page can subscribe and react
   * when the action arrives, regardless of timing.
   */
  private _pendingNotificationAction$ = new BehaviorSubject<Record<string, string> | null>(null);

  /**
   * Observable for pending notification actions.
   * Subscribe to this in the chat page to handle actions reactively.
   */
  get pendingNotificationAction$(): Observable<Record<string, string> | null> {
    return this._pendingNotificationAction$.asObservable();
  }

  constructor(
    private apiService: NutritionAmbitionApiService,
    private platformService: PlatformService,
    private modalController: ModalController,
    private accountsService: AccountsService,
    private router: Router,
    private ngZone: NgZone
  ) {
    // Subscribe to account changes to re-register token when account is created/changed
    this.setupAccountChangeListener();

    // Set up notification tap listener IMMEDIATELY on service creation
    // This must happen before initializePush() to catch taps that launch the app from closed state
    if (this.platformService.isNative()) {
      this.setupNotificationTapListener();
    }
  }

  /**
   * Sets up listener for account changes.
   * When an account changes, re-register the device to associate with new account.
   */
  private setupAccountChangeListener(): void {
    if (this.accountSubscriptionSetUp) return;
    this.accountSubscriptionSetUp = true;

    this.accountsService.account$.subscribe(async (account) => {
      if (!account) return; // Only care when account becomes available
      if (!this.platformService.isNative()) return;
      if (!this.isInitialized) return; // Wait for push to be initialized first

      // Trigger re-registration to associate device with current account
      const permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive === 'granted') {
        await PushNotifications.register();
      }
    });
  }

  /**
   * Initialize push notifications on app start.
   * IMPORTANT: This method must NEVER trigger the OS permission dialog.
   * It only checks existing permission state and registers token if already granted.
   */
  async initializePush(): Promise<void> {
    // Only run on native platforms
    if (!this.platformService.isNative()) {
      return;
    }

    // Prevent double initialization
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;

    try {
      // Set up token refresh listener
      this.setupTokenRefreshListener();

      // Check existing permission state (do NOT request permission)
      const permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'granted') {
        // Already have permission - register for push to get current token
        await PushNotifications.register();
      }
      // If not granted, do nothing - wait for modal flow to request permission
    } catch {
      // Silently fail - push notifications are not critical
    }
  }

  /**
   * Shows the notification permission prompt modal if appropriate.
   * Call this after a successful food log on native platforms.
   */
  async maybeShowPermissionPrompt(): Promise<void> {
    // Only run on native platforms
    if (!this.platformService.isNative()) {
      return;
    }

    // Check if user has already seen the prompt
    const hasSeenPrompt = await this.getHasSeenNotificationPrompt();
    if (hasSeenPrompt) {
      return;
    }

    // Check if already have permission (no need to show modal)
    const permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'granted') {
      // Already granted - mark as seen and ensure token is registered
      await this.setHasSeenNotificationPrompt(true);
      await PushNotifications.register();
      return;
    }

    // Show the permission modal
    const modal = await this.modalController.create({
      component: NotificationPermissionModalComponent,
      backdropDismiss: true,
      cssClass: 'notification-permission-modal'
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    // Set flag after user interaction (either button)
    await this.setHasSeenNotificationPrompt(true);

    if (data?.enableNotifications) {
      // User chose to enable - request permission and register
      await this.requestPermissionAndRegister();
    }
    // If user chose "Not Now", just dismiss - flag is already set
  }

  /**
   * Requests notification permission and registers for push notifications.
   * This triggers the OS permission dialog.
   */
  async requestPermissionAndRegister(): Promise<boolean> {
    if (!this.platformService.isNative()) {
      return false;
    }

    try {
      // Request permission (shows OS dialog)
      const permStatus = await PushNotifications.requestPermissions();

      if (permStatus.receive === 'granted') {
        // Register for push notifications - this triggers the 'registration' event
        await PushNotifications.register();
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  /**
   * Sets up listener for notification taps.
   * MUST be called immediately on service creation to catch taps that launch the app.
   */
  private setupNotificationTapListener(): void {
    // Listen for notification tap (app opened from notification)
    PushNotifications.addListener('pushNotificationActionPerformed', async (action) => {
      await this.handleNotificationTap(action.notification?.data);
    });
  }

  /**
   * Sets up listener for token refresh events.
   * When a new token is received, it's registered with the backend.
   */
  private setupTokenRefreshListener(): void {
    // Listen for successful registration (new token)
    PushNotifications.addListener('registration', async (token: Token) => {
      // Validate token - FCM tokens are typically 150+ chars
      // APNs tokens are 64 hex chars - if we get one of those, the native code isn't converting properly
      if (token.value && token.value.length < 100) {
        // Token too short - appears to be APNs token, not FCM token
        return;
      }

      if (token.value) {
        await this.registerTokenWithBackend(token.value);
      }
    });

    // Listen for registration errors - silently ignore
    PushNotifications.addListener('registrationError', () => {
      // Registration failed - nothing to do
    });

    // Listen for push notifications received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', () => {
      // Do nothing - let user interact naturally. They'll see the notification if they
      // swipe down, and can tap it then. Acting on foreground notifications feels glitchy.
    });
  }

  /**
   * Registers the FCM token with the backend.
   * Always registers - the backend upsert handles duplicates gracefully.
   */
  async registerTokenWithBackend(fcmToken: string): Promise<void> {
    if (!fcmToken) {
      return;
    }

    // Defensive check - only register on native platforms
    const platform = this.platformService.getPlatform();
    if (platform !== 'ios' && platform !== 'android') {
      return;
    }

    try {
      // Get or create install ID
      const installId = await this.getOrCreateInstallId();

      // Register with backend (upsert by installId - no duplicates)
      const request = new RegisterDeviceRequest({
        installId: installId,
        platform: platform,
        fcmToken: fcmToken
      });

      await firstValueFrom(this.apiService.registerDevice(request));
    } catch {
      // Silently fail - push registration is not critical
    }
  }

  /**
   * Gets or creates a unique install ID.
   * This ID persists across app restarts.
   */
  private async getOrCreateInstallId(): Promise<string> {
    const { value } = await Preferences.get({ key: STORAGE_KEYS.INSTALL_ID });

    if (value) {
      return value;
    }

    // Generate a new UUID
    const newInstallId = this.generateUUID();
    await Preferences.set({ key: STORAGE_KEYS.INSTALL_ID, value: newInstallId });

    return newInstallId;
  }

  /**
   * Checks if the user has seen the notification permission prompt.
   */
  private async getHasSeenNotificationPrompt(): Promise<boolean> {
    const { value } = await Preferences.get({ key: STORAGE_KEYS.HAS_SEEN_NOTIFICATION_PROMPT });
    return value === 'true';
  }

  /**
   * Sets whether the user has seen the notification permission prompt.
   */
  private async setHasSeenNotificationPrompt(hasSeen: boolean): Promise<void> {
    await Preferences.set({ key: STORAGE_KEYS.HAS_SEEN_NOTIFICATION_PROMPT, value: hasSeen.toString() });
  }

  /**
   * Generates a UUID v4.
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Consumes and returns any pending notification action data.
   * Returns null if no pending action. Clears the pending action after retrieval.
   * Call this from the chat page when it enters to check for pending actions.
   */
  consumePendingNotificationAction(): Record<string, string> | null {
    const action = this._pendingNotificationAction$.getValue();
    if (action) {
      this._pendingNotificationAction$.next(null);
    }
    return action;
  }

  /**
   * Handles notification tap actions.
   * Stores action data in service state and navigates to the appropriate route.
   * The chat page will consume the action data via consumePendingNotificationAction().
   *
   * Data payload options:
   * - route: Frontend route to navigate to (e.g., '/app/chat', '/app/account')
   * - requiresAction: If 'true', chat page will call backend NotificationAction endpoint
   * - action: Action type for backend routing (e.g., 'test', 'log_favorite')
   * - Additional action-specific fields (e.g., favoriteId)
   */
  private async handleNotificationTap(data: Record<string, string> | undefined): Promise<void> {
    if (!data) {
      return;
    }

    try {
      // Filter to only string values (iOS adds nested 'aps' object that breaks serialization)
      const stringData: Record<string, string> = {};
      for (const [key, value] of Object.entries(data)) {
        if (typeof value === 'string') {
          stringData[key] = value;
        }
      }

      // Navigate to specified route, or default to chat
      const route = stringData['route'] || '/app/chat';
      const requiresAction = stringData['requiresAction'] === 'true';

      // Store action data IMMEDIATELY and SYNCHRONOUSLY - before any async operations
      // This is critical for cold start where Angular may not be fully initialized
      if (requiresAction) {
        this._pendingNotificationAction$.next(stringData);
      }

      // Navigate inside Angular zone to ensure change detection
      this.ngZone.run(async () => {
        await this.router.navigateByUrl(route);

        // For background case: re-emit the action after a short delay
        // This ensures the subscription picks it up even if navigation didn't trigger lifecycle hooks
        if (requiresAction) {
          setTimeout(() => {
            const currentValue = this._pendingNotificationAction$.getValue();
            if (currentValue !== null) {
              this._pendingNotificationAction$.next(currentValue);
            }
          }, 200);
        }
      });
    } catch (err) {
      console.error('[PushNotification] Error in handleNotificationTap:', err);
    }
  }
}
